import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface FocusContextType {
  registerOverlay: (id: string) => void;
  unregisterOverlay: (id: string) => void;
  hasActiveOverlay: boolean;
  activeOverlays: Set<string>;
}

const FocusContext = createContext<FocusContextType | null>(null);

export const FocusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeOverlays, setActiveOverlays] = useState<Set<string>>(new Set());
  const overlaysRef = useRef<Set<string>>(new Set());

  const registerOverlay = useCallback((id: string) => {
    overlaysRef.current.add(id);
    setActiveOverlays(new Set(overlaysRef.current));
    
    // Release Unity capture when an overlay opens
    if (window.sendMessageToUnity) {
      try {
        const captureKeyboardData = {
          eventType: 'CaptureKeyboard',
          payload: JSON.stringify({ capture: false })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
        
        const captureCursorData = {
          eventType: 'CaptureCursor',
          payload: JSON.stringify({ capture: false })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
      } catch (error) {
        console.error('[FocusContext] Error releasing Unity capture:', error);
      }
    }
  }, []);

  const unregisterOverlay = useCallback((id: string) => {
    // Only proceed if the overlay was actually registered
    if (!overlaysRef.current.has(id)) {
      return;
    }
    
    overlaysRef.current.delete(id);
    setActiveOverlays(new Set(overlaysRef.current));
    
    // Re-enable Unity capture if no overlays are active
    if (overlaysRef.current.size === 0 && window.sendMessageToUnity) {
      try {
        const captureKeyboardData = {
          eventType: 'CaptureKeyboard',
          payload: JSON.stringify({ capture: true })
        };
        window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureKeyboardData));
        
        // const captureCursorData = {
        //   eventType: 'CaptureCursor',
        //   payload: JSON.stringify({ capture: true })
        // };
        // window.sendMessageToUnity('BrowserBridge', 'ReceivePayload', JSON.stringify(captureCursorData));
      } catch (error) {
        console.error('[FocusContext] Error enabling Unity capture:', error);
      }
    }
  }, []);

  const hasActiveOverlay = activeOverlays.size > 0;

  return (
    <FocusContext.Provider value={{ registerOverlay, unregisterOverlay, hasActiveOverlay, activeOverlays }}>
      {children}
    </FocusContext.Provider>
  );
};

// Hook to use the focus context
export const useFocus = (): FocusContextType => {
  const context = useContext(FocusContext);
  if (!context) {
    throw new Error('useFocus must be used within a FocusProvider');
  }
  return context;
};

// Convenience hook for overlays - automatically registers/unregisters based on isOpen state
export const useOverlay = (id: string, isOpen: boolean): void => {
  const { registerOverlay, unregisterOverlay } = useFocus();

  useEffect(() => {
    if (isOpen) {
      registerOverlay(id);
      // Only unregister via cleanup when overlay was actually registered
      return () => {
        unregisterOverlay(id);
      };
    }
    // No cleanup needed if overlay wasn't registered
  }, [id, isOpen, registerOverlay, unregisterOverlay]);
};

export default FocusContext;

